package com.sddamico.draftmachine.components.sections

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.sddamico.draftmachine.components.layouts.MobileFrame
import com.sddamico.draftmachine.data.DraftStatus
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.backgroundImageRaw
import com.sddamico.draftmachine.ds.displayBlock
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.dmPanel
import com.sddamico.draftmachine.ds.feedback.EmptyState
import com.sddamico.draftmachine.ds.forms.Button
import com.sddamico.draftmachine.ds.forms.IconButton
import com.sddamico.draftmachine.ds.forms.ButtonSize
import com.sddamico.draftmachine.ds.forms.ButtonVariant
import com.sddamico.draftmachine.ds.motion.Motion
import com.sddamico.draftmachine.ds.motion.TransitionRole
import com.sddamico.draftmachine.ds.motion.ViewTransitions
import com.sddamico.draftmachine.ds.motion.morphKey
import com.sddamico.draftmachine.ds.motion.transitionRole
import com.sddamico.draftmachine.ds.roleH3
import com.sddamico.draftmachine.ds.spin
import com.sddamico.draftmachine.models.DraftSummary
import com.sddamico.draftmachine.models.LeagueCardResponse
import com.sddamico.draftmachine.util.activeMain
import com.sddamico.draftmachine.util.draftMorphKey
import com.sddamico.draftmachine.util.formatShortDate
import com.sddamico.draftmachine.util.orderPrefix
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

private const val DEFAULT_VISIBLE_DRAFTS = 3

/**
 * The Machine room dashboard (frames 1f / 1g). Renders the league cards, or the first-run empty
 * state when [cards] is empty. Presentational only — the caller loads [cards] and supplies the
 * navigation callbacks.
 */
@Composable
fun MachineRoomSection(
  email: String,
  cards: List<LeagueCardResponse>,
  onStartDraft: () -> Unit,
  onOpenDraft: (String) -> Unit,
  onAccount: () -> Unit,
  onLeagueSettings: (String) -> Unit,
) {
  MobileFrame(
    modifier = Modifier.displayFlex().flexDirection(FlexDirection.Column),
    header = { AppHeader(email = email, onAccount = onAccount, nav = AppNav.Drafts) },
  ) {
    if (cards.isEmpty()) EmptyDashboard(onStartDraft)
    else DashboardBody(cards, onStartDraft, onOpenDraft, onLeagueSettings)
  }
}

@Composable
private fun EmptyDashboard(onStartDraft: () -> Unit) {
  Div(
    attrs = Modifier.flex(1).displayFlex().flexDirection(FlexDirection.Column)
      .alignItems(AlignItems.Center).justifyContent(JustifyContent.Center)
      .padding(24.px).toAttrs()
  ) {
    EmptyState(
      icon = "gear-six",
      title = "No leagues yet",
      message = "Spin up your first to get the machine running.",
      action = {
        Button(
          "Start a draft", variant = ButtonVariant.Primary, size = ButtonSize.Lg,
          icon = "plus", onClick = onStartDraft,
        )
      },
    )
  }
}

@Composable
private fun DashboardBody(
  cards: List<LeagueCardResponse>,
  onStartDraft: () -> Unit,
  onOpenDraft: (String) -> Unit,
  onLeagueSettings: (String) -> Unit,
) {
  Div(
    attrs = Modifier.flex(1).displayFlex().flexDirection(FlexDirection.Column).gap(12.px)
      .padding(top = 22.px, leftRight = 16.px, bottom = 24.px).toAttrs()
  ) {
    Div(
      attrs = Modifier.displayFlex().alignItems(AlignItems.FlexEnd)
        .justifyContent(JustifyContent.SpaceBetween).gap(12.px).margin(bottom = 6.px).toAttrs()
    ) {
      SpanText(
        "Leagues",
        Modifier.fontFamily(*DmType.display).fontSize(28.px).lineHeight(1).color(DmColor.text),
      )
      Button("Start a draft", variant = ButtonVariant.Primary, icon = "plus", onClick = onStartDraft)
    }
    cards.forEach { LeagueCard(it, onOpenDraft, onLeagueSettings) }
  }
}

@Composable
private fun LeagueCard(
  card: LeagueCardResponse,
  onOpenDraft: (String) -> Unit,
  onLeagueSettings: (String) -> Unit,
) {
  var expanded by remember(card.league.id) { mutableStateOf(false) }
  val visibleDrafts = if (expanded) card.drafts else card.drafts.take(DEFAULT_VISIBLE_DRAFTS)

  Div(attrs = Modifier.dmPanel().padding(14.px).transitionRole(TransitionRole.Item).toAttrs()) {
    Div(attrs = Modifier.displayFlex().alignItems(AlignItems.FlexStart).gap(10.px).toAttrs()) {
      Div(attrs = Modifier.flex(1).minWidth(0.px).toAttrs()) {
        SpanText(card.league.name, Modifier.roleH3().displayBlock().color(DmColor.text))
        if (card.youManage) {
          SpanText(
            "YOU MANAGE",
            Modifier.fontFamily(*DmType.mono).fontSize(11.px).color(DmColor.textMuted)
              .displayBlock().margin(top = 3.px),
          )
        }
      }
      // Only a manager can touch the league's external connection, so only they get the gear.
      if (card.youManage) {
        IconButton(
          icon = "gear-six",
          label = "League settings",
          size = ButtonSize.Sm,
          variant = ButtonVariant.Ghost,
          onClick = { onLeagueSettings(card.league.id) },
        )
      }
    }
    visibleDrafts.forEach { key(it.draftId) { DraftProgressRow(it, onOpenDraft) } }
    if (card.drafts.size > DEFAULT_VISIBLE_DRAFTS) {
      Button(
        if (expanded) "View less" else "View more",
        variant = ButtonVariant.Ghost,
        size = ButtonSize.Sm,
        iconRight = if (expanded) "caret-up" else "caret-down",
        fullWidth = true,
        onClick = { ViewTransitions.run(Motion.Update) { expanded = !expanded } },
        modifier = Modifier.margin(top = 8.px),
      )
    }
  }
}

@Composable
private fun DraftProgressRow(d: DraftSummary, onOpenDraft: (String) -> Unit) {
  when (d.status) {
    DraftStatus.ACTIVE -> ProgressRow(
      iconName = "gear-six", iconWeight = IconWeight.Fill, iconColor = DmColor.glass400, spinning = true,
      mainText = activeMain(d), mainColor = DmColor.text,
      subText = "ON THE CLOCK: ${(d.onClockTeamName ?: "").uppercase()}", subColor = DmColor.glass300,
      energized = true, onClick = { onOpenDraft(d.draftId) }, morph = draftMorphKey(d.draftId),
    )
    DraftStatus.SETUP -> ProgressRow(
      iconName = "gear-six", iconWeight = IconWeight.Bold, iconColor = DmColor.steel500, spinning = false,
      mainText = setupMain(d), mainColor = DmColor.textSecondary,
      subText = if (d.totalPicks == 0) "ORDER NOT SET" else "READY TO START", subColor = DmColor.textMuted,
      energized = false, onClick = { onOpenDraft(d.draftId) }, morph = draftMorphKey(d.draftId),
    )
    DraftStatus.COMPLETE -> ProgressRow(
      iconName = "check-circle", iconWeight = IconWeight.Fill, iconColor = DmColor.verde400, spinning = false,
      mainText = completeMain(d), mainColor = DmColor.textSecondary,
      subText = "COMPLETED DRAFT", subColor = DmColor.textMuted,
      energized = false, onClick = { onOpenDraft(d.draftId) }, morph = draftMorphKey(d.draftId),
    )
    DraftStatus.CANCELLED -> ProgressRow(
      iconName = "prohibit", iconWeight = IconWeight.Bold, iconColor = DmColor.rust400, spinning = false,
      mainText = cancelledMain(d), mainColor = DmColor.textSecondary,
      subText = "CANCELLED DRAFT", subColor = DmColor.rust300,
      energized = false, onClick = { onOpenDraft(d.draftId) }, morph = draftMorphKey(d.draftId),
    )
  }
}

@Composable
private fun ProgressRow(
  iconName: String,
  iconWeight: IconWeight,
  iconColor: Color,
  spinning: Boolean,
  mainText: String,
  mainColor: Color,
  subText: String,
  subColor: Color,
  energized: Boolean,
  onClick: () -> Unit,
  morph: String,
) {
  val base = Modifier.displayFlex().alignItems(AlignItems.Center).gap(10.px)
    .transitionRole(TransitionRole.Item).morphKey(morph)
    .margin(top = 12.px).padding(12.px).borderRadius(DmEffects.radiusSm).cursor(Cursor.Pointer)
  val surface = if (energized) {
    base.border(1.px, LineStyle.Solid, DmColor.glass600)
      .backgroundColor(Color.rgba(47, 166, 168, 0.07f))
      .backgroundImageRaw(DmEffects.gradientEnergy)
  } else {
    base.border(1.px, LineStyle.Solid, DmColor.border).backgroundColor(DmColor.surfaceInset)
  }

  Div(attrs = surface.toAttrs { onClick { onClick() } }) {
    Icon(
      iconName, iconWeight,
      Modifier.fontSize(18.px).color(iconColor).flex("0 0 auto")
        .then(if (spinning) Modifier.spin("9s") else Modifier),
    )
    Div(attrs = Modifier.flex(1).minWidth(0.px).toAttrs()) {
      SpanText(
        mainText,
        Modifier.fontFamily(*DmType.body).fontSize(13.px).fontWeight(DmType.weightSemibold)
          .color(mainColor).displayBlock(),
      )
      SpanText(
        subText,
        Modifier.fontFamily(*DmType.mono).fontSize(11.px).color(subColor)
          .displayBlock().margin(top = 2.px),
      )
    }
    Icon(
      "caret-right", IconWeight.Bold,
      Modifier.fontSize(16.px).color(DmColor.steel500).flex("0 0 auto"),
    )
  }
}

private fun setupMain(d: DraftSummary): String =
  "${orderPrefix(d.draftOrder)}${d.rounds} rounds · finishing setup"

private fun completeMain(d: DraftSummary): String {
  val date = d.completedAtIso?.let(::formatShortDate)?.takeIf { it.isNotEmpty() }
  val tail = if (date != null) " · completed $date" else " · complete"
  return "${orderPrefix(d.draftOrder)}${d.rounds} rounds$tail"
}

private fun cancelledMain(d: DraftSummary): String {
  val date = d.completedAtIso?.let(::formatShortDate)?.takeIf { it.isNotEmpty() }
  val tail = if (date != null) " · cancelled $date" else " · cancelled"
  return "${orderPrefix(d.draftOrder)}${d.rounds} rounds$tail"
}
